
import React from "react";
import { Link } from "react-router-dom";
import {
  Instagram,
  Linkedin,
  Shield,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";

import { useLanguage } from "../context/LanguageContext";

const SOCIAL_LINKS = [
  {
    name: "Instagram",
    url: "https://www.instagram.com/frontspace__?vrfl=d3lsa3Zna2ZnZGZz",
    icon: Instagram,
  },
  {
    name: "LinkedIn",
    url: "https://www.linkedin.com/company/140233961",
    icon: Linkedin,
  },
];

export default function Footer() {
  const { t } = useLanguage();

  // Navigation order:
  // Home, About Us, Services, News, Contact
  const companyLinks = [
    { to: "/", label: t.nav.home || "Home" },
    { to: "/aboutus", label: t.nav.about || "About Us" },
    { to: "/services", label: t.nav.services || "Services" },
    { to: "/news", label: t.nav.news || "News" },
    { to: "/contact", label: t.nav.contact || "Contact" },
  ];

  return (
    <footer className="bg-[#050814] border-t border-[#00F0FF]/10 mt-auto">
      <div className="max-w-7xl mx-auto px-6 py-16">

        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">

          {/* BRAND AND CONTACT */}
          <div className="md:col-span-2">
            <Link
              to="/"
              className="inline-flex items-center gap-2 mb-4"
              aria-label="FrontSpace Home"
            >
              <Shield size={22} className="text-[#00F0FF]" />
              <span
                className="text-xl font-black tracking-tighter text-white"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                FRONT<span className="text-[#00F0FF]">SPACE</span>
              </span>
            </Link>

            <p className="text-slate-400 text-sm leading-relaxed max-w-sm mt-3">
              FrontSpace develops custom software, AI-powered solutions,
              business automation, and cybersecurity technologies that
              help organizations innovate and operate more efficiently.
            </p>

            <div className="mt-6 space-y-3">
              <div className="flex items-start gap-2 text-slate-400 text-sm">
                <MapPin
                  size={15}
                  className="text-[#00F0FF] mt-0.5 flex-shrink-0"
                />
                <span>{t.contact.address}</span>
              </div>

              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <Phone
                  size={15}
                  className="text-[#00F0FF] flex-shrink-0"
                />
                <a
                  href="tel:+355682008275"
                  className="hover:text-[#00F0FF] transition-colors"
                >
                  +355 68 200 8275
                </a>
              </div>

              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <Mail
                  size={15}
                  className="text-[#00F0FF] flex-shrink-0"
                />
                <a
                  href="mailto:office@frontspace.io"
                  className="hover:text-[#00F0FF] transition-colors"
                >
                  office@frontspace.io
                </a>
              </div>
            </div>

            {/* SOCIAL MEDIA */}
            <div className="flex items-center gap-3 mt-6">
              {SOCIAL_LINKS.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.name}
                    title={social.name}
                    className="social-icon-btn"
                  >
                    <Icon size={17} />
                  </a>
                );
              })}

              <a
                href="https://wa.me/355682008275"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                title="WhatsApp"
                className="social-icon-btn"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="17"
                  height="17"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </a>
            </div>
          </div>

          {/* COMPANY LINKS */}
          <div>
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">
              {t.footer.companyLinks}
            </h4>

            <ul className="space-y-3">
              {companyLinks.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="text-slate-400 hover:text-[#00F0FF] text-sm transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* LEGAL LINKS */}
          <div>
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">
              {t.footer.legalLinks}
            </h4>

            <ul className="space-y-3">
              <li>
                <Link
                  to="/terms"
                  className="text-slate-400 hover:text-[#00F0FF] text-sm transition-colors"
                >
                  {t.footer.terms}
                </Link>
              </li>
              <li>
                <Link
                  to="/cookies"
                  className="text-slate-400 hover:text-[#00F0FF] text-sm transition-colors"
                >
                  {t.footer.cookies}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* BOTTOM BAR */}
        <div className="cyber-divider mb-8" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} FrontSpace. All rights reserved.
          </p>

          <div className="flex items-center gap-6">
            <Link
              to="/terms"
              className="text-slate-500 hover:text-[#00F0FF] text-xs transition-colors"
            >
              {t.footer.terms}
            </Link>
            <Link
              to="/cookies"
              className="text-slate-500 hover:text-[#00F0FF] text-xs transition-colors"
            >
              {t.footer.cookies}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
